import {
  IonButton,
  IonIcon,
  IonLabel,
  IonPopover,
  IonList,
  IonItem,
} from '@ionic/react';
import {
  calendarOutline,
  cashOutline,
  locationOutline,
  peopleOutline,
  ellipsisVertical,
  documentTextOutline,
  sparklesOutline,
  briefcaseOutline,
  heartOutline,
  heart
} from 'ionicons/icons';
import React from 'react';

export interface Job {
  id: string;
  title: string;
  company: string;
  location: string;
  salary_min?: number;
  salary_max?: number;
  date_posted: string;
  job_type?: string;
  contacts_count?: number;
  match_percentage: number;
  job_url?: string;
  site?: string;
  is_remote?: boolean;
  description?: string;
  currency?: string;
  salary_source?: string;
}

interface JobCardProps {
  job: Job;
  isActive: boolean;
  onClick: (jobId: string) => void;
  showDetailsButton?: boolean;
  detailsUrl?: string;
  onOpenOriginal?: (jobId: string, jobUrl: string) => void;
  onFitAnalysis?: (jobId: string) => void;
  onCoverLetter?: (jobId: string) => void;
  isFavorite?: boolean;
  onToggleFavorite?: (jobId: string) => void;
}

// Helper to format salary
const formatSalary = (min?: number, max?: number) => {
  if (!min && !max) return '';
  if (min && max) return `$${Math.round(min / 1000)}k - $${Math.round(max / 1000)}k`;
  if (min) return `$${Math.round(min / 1000)}k+`;
  if (max) return `Up to $${Math.round(max / 1000)}k`;
  return '';
};

// Helper to get match percentage color
const getMatchColor = (percentage: number) => {
  if (percentage >= 90) return { bg: '#dcfce7', text: '#166534' }; // Green-100 to Green-800
  if (percentage >= 80) return { bg: '#dbeafe', text: '#1e40af' }; // Blue-100 to Blue-800
  if (percentage >= 70) return { bg: '#fef9c3', text: '#854d0e' }; // Yellow-100 to Yellow-800
  return { bg: '#f3f4f6', text: '#374151' }; // Gray-100 to Gray-700
};

// Helper to determine posting freshness
const getPostingFreshness = (datePosted: string) => {
  const posted = new Date(datePosted);
  const diffMs = Date.now() - posted.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffHours / 24);

  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return '1 day ago';
  return `${diffDays} days ago`;
};

const JobCard: React.FC<JobCardProps> = ({ job, isActive, onClick, showDetailsButton, detailsUrl, onOpenOriginal, onFitAnalysis, onCoverLetter, isFavorite, onToggleFavorite }) => {
  const [isHovered, setIsHovered] = React.useState(false);
  const [activeTooltip, setActiveTooltip] = React.useState<string | null>(null);
  const [showMenu, setShowMenu] = React.useState(false);
  const menuButtonRef = React.useRef<HTMLIonButtonElement>(null);

  const handleCardClick = () => {
    onClick(job.id);
  };

  const handleMenuClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMenu(true);
  };

  const handleFitAnalysis = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMenu(false);
    if (onFitAnalysis) onFitAnalysis(job.id);
  };

  const handleCoverLetter = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMenu(false);
    if (onCoverLetter) onCoverLetter(job.id);
  };

  const matchStyle = getMatchColor(job.match_percentage || 0);
  const salary = formatSalary(job.salary_min, job.salary_max);
  const timeAgo = getPostingFreshness(job.date_posted);

  return (
    <div
      id={`job-${job.id}`}
      className="group"
      style={{
        marginBottom: '12px',
        cursor: 'pointer',
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        border: isActive ? '1.5px solid #3b82f6' : '1px solid #e5e7eb',
        padding: '16px',
        transition: 'all 0.2s ease',
        boxShadow: isActive ? '0 4px 12px rgba(59, 130, 246, 0.15)' : isHovered ? '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)' : 'none',
        position: 'relative'
      }}
      onClick={handleCardClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Header Row: Title and Match Score */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
        <h3 style={{
          margin: 0,
          fontSize: '16px',
          fontWeight: '600',
          color: '#111827',
          lineHeight: '1.4',
          flex: 1,
          paddingRight: '12px'
        }}>
          {job.title}
        </h3>
        {job.match_percentage > 0 && (
          <span style={{
            backgroundColor: matchStyle.bg,
            color: matchStyle.text,
            fontSize: '12px',
            fontWeight: '600',
            padding: '2px 8px',
            borderRadius: '6px',
            whiteSpace: 'nowrap'
          }}>
            {job.match_percentage}%
          </span>
        )}
      </div>

      {/* Company Name */}
      <div style={{ marginBottom: '12px' }}>
        <span style={{
          color: '#2563eb',
          fontWeight: '500',
          fontSize: '14px'
        }}>
          {job.company}
        </span>
      </div>

      {/* Metadata Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '8px 16px',
        marginBottom: '16px'
      }}>
        {/* Location */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#6b7280', fontSize: '13px' }}>
          <IonIcon icon={locationOutline} style={{ fontSize: '16px' }} />
          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {job.is_remote ? 'Remote' : job.location}
          </span>
        </div>

        {/* Salary (if available) */}
        {salary && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#6b7280', fontSize: '13px' }}>
            <IonIcon icon={cashOutline} style={{ fontSize: '16px' }} />
            <span>{salary}</span>
          </div>
        )}

        {/* Time Posted */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#6b7280', fontSize: '13px' }}>
          <IonIcon icon={calendarOutline} style={{ fontSize: '16px' }} />
          <span>{timeAgo}</span>
        </div>

        {/* Job Type */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#6b7280', fontSize: '13px' }}>
          <IonIcon icon={briefcaseOutline} style={{ fontSize: '16px' }} />
          <span style={{ textTransform: 'capitalize' }}>{job.job_type || 'Full-time'}</span>
        </div>
      </div>

      {/* Footer: Contacts and Actions */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {/* Contacts Badge */}
        {job.contacts_count !== undefined && job.contacts_count > 0 ? (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            backgroundColor: '#f3e8ff', // Purple-100
            color: '#7e22ce', // Purple-700
            padding: '4px 8px',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: '500'
          }}>
            <IonIcon icon={peopleOutline} style={{ fontSize: '14px' }} />
            <span>{job.contacts_count} contact{job.contacts_count !== 1 ? 's' : ''}</span>
          </div>
        ) : (
          <div></div> // Spacer
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: '8px' }}>
          {/* Favorite Button */}
          <IonButton
            fill="clear"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              if (onToggleFavorite) onToggleFavorite(job.id);
            }}
            style={{
              '--padding-start': '8px',
              '--padding-end': '8px',
              height: '32px',
              color: isFavorite ? '#ef4444' : '#9ca3af',
              margin: 0
            }}
          >
            <IonIcon icon={isFavorite ? heart : heartOutline} style={{ fontSize: '20px' }} />
          </IonButton>

          {/* Fit Analysis */}
          <div
            style={{ position: 'relative' }}
            onMouseEnter={() => setActiveTooltip('fit')}
            onMouseLeave={() => setActiveTooltip(null)}
          >
            <IonButton
              fill="clear"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                if (onFitAnalysis) onFitAnalysis(job.id);
              }}
              style={{
                '--padding-start': '8px',
                '--padding-end': '8px',
                height: '32px',
                color: '#6366f1', // Indigo
                margin: 0,
                '--background-hover': 'rgba(99, 102, 241, 0.1)'
              }}
            >
              <IonIcon icon={sparklesOutline} style={{ fontSize: '18px' }} />
            </IonButton>
            {activeTooltip === 'fit' && (
              <div style={{
                position: 'absolute',
                bottom: '100%',
                left: '50%',
                transform: 'translateX(-50%)',
                marginBottom: '8px',
                padding: '4px 8px',
                backgroundColor: '#1f2937',
                color: 'white',
                fontSize: '12px',
                fontWeight: '500',
                borderRadius: '6px',
                whiteSpace: 'nowrap',
                zIndex: 10,
                pointerEvents: 'none',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
              }}>
                Am I a Fit?
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  borderLeft: '4px solid transparent',
                  borderRight: '4px solid transparent',
                  borderTop: '4px solid #1f2937'
                }} />
              </div>
            )}
          </div>

          {/* Cover Letter */}
          <div
            style={{ position: 'relative' }}
            onMouseEnter={() => setActiveTooltip('cover')}
            onMouseLeave={() => setActiveTooltip(null)}
          >
            <IonButton
              fill="clear"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                if (onCoverLetter) onCoverLetter(job.id);
              }}
              style={{
                '--padding-start': '8px',
                '--padding-end': '8px',
                height: '32px',
                color: '#10b981', // Emerald
                margin: 0,
                '--background-hover': 'rgba(16, 185, 129, 0.1)'
              }}
            >
              <IonIcon icon={documentTextOutline} style={{ fontSize: '18px' }} />
            </IonButton>
            {activeTooltip === 'cover' && (
              <div style={{
                position: 'absolute',
                bottom: '100%',
                left: '50%',
                transform: 'translateX(-50%)',
                marginBottom: '8px',
                padding: '4px 8px',
                backgroundColor: '#1f2937',
                color: 'white',
                fontSize: '12px',
                fontWeight: '500',
                borderRadius: '6px',
                whiteSpace: 'nowrap',
                zIndex: 10,
                pointerEvents: 'none',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
              }}>
                Cover Letter
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  borderLeft: '4px solid transparent',
                  borderRight: '4px solid transparent',
                  borderTop: '4px solid #1f2937'
                }} />
              </div>
            )}
          </div>

          {/* View Job - Only show if URL exists */}
          {job.job_url ? (
            <IonButton
              fill="solid"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                if (onOpenOriginal) {
                  onOpenOriginal(job.id, job.job_url);
                } else {
                  window.open(job.job_url, '_blank', 'noopener,noreferrer');
                }
              }}
              style={{
                '--background': '#111827',
                '--background-activated': '#4b5563', // Gray-600 for click feedback
                '--background-hover': '#1f2937', // Gray-800 for hover
                '--border-radius': '6px',
                '--padding-start': '12px',
                '--padding-end': '12px',
                height: '32px',
                fontSize: '13px',
                fontWeight: '500',
                margin: 0
              }}
            >
              View Job
            </IonButton>
          ) : (
            <span style={{
              fontSize: '12px',
              color: '#9ca3af',
              fontStyle: 'italic',
              padding: '6px 12px'
            }}>
              No URL available
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default JobCard;